import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Check, CreditCard, Calendar, TrendingUp, AlertTriangle, ChevronRight, ChevronDown, Clock, Shield, X, Star, MessageCircle, Users } from 'lucide-react';
import PricingTabs from '../components/PricingTabs';
import CockpitProofCarousel from '../components/CockpitProofCarousel';
import DashboardPilotageSection from '../components/DashboardPilotageSection';
import RealDashboardShowcase from '../components/RealDashboardShowcase';
import AnimatedAvatar from '../components/AnimatedAvatar';
import SabrilCloserBubble from '../components/SabrilCloserBubble';
import FranceClosureMap from '../components/FranceClosureMap';
import OwnerRealizationSection from '../components/OwnerRealizationSection';
import OffersQuickAccess from '../components/OffersQuickAccess';
import SabrilPixarAvatar from '../components/SabrilPixarAvatar';
import PricingRevealGate from '../components/PricingRevealGate';
import PremiumDarkAtmosphere from '../components/PremiumDarkAtmosphere';
import { readUserContext, getSuggestedOffer, getSuggestedOfferLabel } from '../lib/userContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_URL = "https://calendly.com/s-jurczeszyn59/30min";
const SPOTS_LEFT = 3; // Places restantes

// Animated Counter
const AnimatedCounter = ({ value, prefix = "", suffix = "", className = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      const duration = 1500;
      const steps = 60;
      const increment = value / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  
  return (
    <span ref={ref} className={className}>
      {prefix}{count.toLocaleString('fr-FR')}{suffix}
    </span>
  );
};

// Pain Point Card with animation
const PainPointCard = ({ icon, title, description, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="bg-white rounded-xl p-5 border-l-4 border-[#F05A1A] flex gap-4 items-start"
    >
      <span className="text-2xl flex-shrink-0">{icon}</span>
      <div>
        <h3 className="text-[#1a1a1a] font-bold text-lg mb-1">{title}</h3>
        <p className="text-[rgba(26,26,26,0.58)] text-sm">{description}</p>
      </div>
    </motion.div>
  );
};

// Module Card with expandable content
const ModuleCard = ({ number, icon, title, weeks, description, isOpen, onToggle, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay }}
      className="bg-white rounded-2xl overflow-hidden border border-[#e0dbd4]"
    >
      <button
        onClick={onToggle}
        className="w-full p-5 flex items-center gap-4 text-left hover:bg-[#f0ede8] transition-colors"
      >
        <span className="text-[#F05A1A] font-bebas text-3xl w-10">{number}</span>
        <span className="text-2xl">{icon}</span>
        <div className="flex-1">
          <h3 className="text-[#1a1a1a] font-bold uppercase tracking-wide">{title}</h3>
          <p className="text-[#F05A1A] text-sm">{weeks}</p>
        </div>
        <ChevronDown className={`w-5 h-5 text-[rgba(26,26,26,0.42)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 text-[rgba(26,26,26,0.58)] text-sm leading-relaxed border-t border-[#e0dbd4] pt-4">
              {description}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Premium Interactive Slider Component
const InteractiveSlider = ({ label, min, max, value, onChange, unit = "€", impact, id }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  
  const handleChange = (e) => {
    const newValue = parseInt(e.target.value, 10);
    onChange(newValue);
  };
  
  return (
    <div className="bg-gradient-to-br from-[#1A1A1A] to-[#141414] rounded-2xl p-6 border border-[#e0dbd4] shadow-xl">
      <div className="flex justify-between items-center mb-6">
        <label htmlFor={id} className="text-white font-semibold text-lg">{label}</label>
        <span className="text-[#F05A1A] font-bebas text-3xl">{value}{unit}</span>
      </div>
      
      {/* Slider with custom styling */}
      <div className="relative">
        {/* Background track */}
        <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-3 bg-[#e0dbd4] rounded-full pointer-events-none" />
        
        {/* Progress fill */}
        <div 
          className="absolute top-1/2 -translate-y-1/2 left-0 h-3 bg-gradient-to-r from-[#F05A1A] to-[#FF7A3D] rounded-full pointer-events-none"
          style={{ width: `${percentage}%` }}
        />
        
        {/* Native input for better mobile support */}
        <input
          id={id}
          name={id}
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={handleChange}
          className="relative w-full h-8 appearance-none bg-transparent cursor-pointer z-10 slider-input"
        />
      </div>
      
      <div className="flex justify-between text-sm text-gray-400 mt-3">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
      
      {impact && (
        <div className="mt-5 pt-5 border-t border-gray-600">
          <p className="text-green-400 font-bold text-center text-lg">{impact}</p>
        </div>
      )}
    </div>
  );
};

// Result Card
const ResultCard = ({ icon, stat, label }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-6 text-center border border-[#e0dbd4]"
  >
    <span className="text-3xl mb-3 block">{icon}</span>
    <p className="text-[#F05A1A] font-bebas text-4xl mb-1">{stat}</p>
    <p className="text-[rgba(26,26,26,0.58)] text-sm">{label}</p>
  </motion.div>
);

// Comparison Row
const ComparisonRow = ({ label, before, after }) => (
  <div className="grid grid-cols-3 gap-4 py-3 border-b border-[#e0dbd4]">
    <span className="text-[rgba(26,26,26,0.58)] text-sm">{label}</span>
    <span className="text-red-400 text-center font-semibold">{before}</span>
    <span className="text-green-400 text-center font-semibold">{after}</span>
  </div>
);

// Badge component
const Badge = ({ children }) => (
  <span className="inline-flex items-center gap-2 bg-[#e0dbd4] text-[#1a1a1a] px-4 py-2 rounded-full text-sm">
    <Check className="w-4 h-4 text-[#F05A1A]" />
    {children}
  </span>
);

// Testimonial Card
const TestimonialCard = ({ name, role, quote, result, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="bg-gradient-to-br from-[#1A1A1A] to-[#141414] rounded-2xl p-6 border border-[#e0dbd4]"
    >
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-5 h-5 fill-[#F05A1A] text-[#F05A1A]" />
        ))}
      </div>
      <p className="text-gray-300 italic mb-4">"{quote}"</p>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-white font-bold">{name}</p>
          <p className="text-gray-400 text-sm">{role}</p>
        </div>
        <div className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-bold">
          {result}
        </div>
      </div>
    </motion.div>
  );
};

// FAQ Item
const FAQItem = ({ question, answer, isOpen, onToggle, dark = false }) => (
  <div className={`border-b ${dark ? 'border-white/10' : 'border-[#e0dbd4]'}`}>
    <button
      onClick={onToggle}
      className="w-full py-5 flex items-center justify-between text-left"
    >
      <span className={`font-semibold pr-4 ${dark ? 'text-white' : 'text-[#1a1a1a]'}`}>{question}</span>
      <ChevronDown className={`w-5 h-5 text-[#F05A1A] transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
        >
          <p className={`pb-5 leading-relaxed ${dark ? 'text-white/65' : 'text-[rgba(26,26,26,0.58)]'}`}>{answer}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// Testimonials data
const TESTIMONIALS = [
  { name: 'Marc D.', role: 'Restaurant Italien, Lyon', quote: "En 6 semaines, on a identifié 3 200€ de pertes par mois. Aujourd'hui mon resto tourne même quand je suis pas là.", result: '+18% CA' },
  { name: 'Sarah L.', role: 'Brasserie, Paris', quote: "La stratégie réseaux + le menu retravaillé m'ont fait gagner 40 nouveaux clients le premier mois. Le ROI est juste énorme.", result: '+32 couverts/sem' },
  { name: 'Thomas R.', role: 'Fast-casual, Bordeaux', quote: "J'ai enfin des process écrits. Mon équipe sait quoi faire. Je peux enfin prendre des vacances.", result: 'Resto autonome' },
  { name: 'Léa M.', role: 'Ouverture Healthy, Marseille', quote: "Sabril a checké tous mes prestataires avant que je signe. Résultat : 12 000€ d'arnaque évitée sur les travaux + 1 fournisseur viré.", result: '−12 000€ d\'arnaque' },
];

// FAQ data
const FAQ_DATA = [
  { question: "C'est quoi exactement l'accompagnement PRSNL ?", answer: "Un programme intensif de 90 jours avec 11 modules opérationnels : finances, carte, image de marque, validation prestataires, réseaux sociaux, fiches techniques, recrutement, organisation, livraison, packaging — plus 2 jours sur place chez toi et un coaching 1-à-1 toutes les 2 semaines." },
  { question: "Quelle différence entre Refonte et Ouverture ?", answer: "La Refonte (2j sur place · 90 jours) c'est pour les restos déjà ouverts qui veulent passer au niveau supérieur. L'Ouverture (9j sur place · 3 mois) c'est pour les futurs restaurateurs — on construit tout de A à Z : concept, déco, identité, équipe, jour J. Les tarifs sont communiqués après un échange de 15 min pour qualifier ton projet." },
  { question: "Et si ça marche pas pour moi ?", answer: "Notre méthode a été affûtée sur 7 marchés ultra-exigeants : Paris, Cannes, Marrakech, Bangtao, New York, Miami, Marbella. C'est ce niveau d'exécution qu'on apporte chez toi, peu importe ta ville. Si tu joues le jeu (présence, exécution, KPIs hebdo), on construit une marque pilotable, duplicable et rentable sur la durée. C'est un engagement long terme, pas un gimmick." },
  { question: "Je n'ai pas le temps, je bosse déjà 60h+", answer: "C'est justement pour ça que tu as besoin de PRSNL. Le module Organisation interne te libère 10-15h/semaine en moyenne." },
  { question: "Possible d'étaler le paiement ?", answer: "Oui, on peut s'arranger sur un paiement échelonné selon ta situation. On en parle directement avec Sabril dans le chat avant que tu valides." },
];

// Modules data — 11 modules (Refonte) alignés avec les feature cards de PricingTabs
const MODULES = [
  { number: '01', icon: '💰', title: 'CHIFFRES & RENTABILITÉ', weeks: 'Semaines 1-2', description: 'Audit complet food cost, masse salariale, prime cost. Objectif 25-30% / 30%. On identifie ce qui mange ta marge et on construit un pilotage industriel — pas un quick win.' },
  { number: '02', icon: '🍽️', title: 'CARTE & MENU GAGNANT', weeks: 'Semaines 3-4', description: '1 plat star · menu phare tendance 2026 · pricing optimal. On retravaille la carte plat par plat pour qu\'elle rapporte vraiment.' },
  { number: '03', icon: '🎨', title: 'IMAGE DE MARQUE & DA', weeks: 'Semaines 3-5', description: 'Direction artistique, logo, charte graphique, cohérence visuelle. Ton resto devient une marque reconnaissable.' },
  { number: '04', icon: '🛡️', title: 'VALIDATION PRESTATAIRES', weeks: 'Continu', description: 'On checke avec toi devis, contrats, artisans, fournisseurs, agences. On évite les arnaques classiques (8-15k€ d\'économies).' },
  { number: '05', icon: '📱', title: 'STRATÉGIE RÉSEAUX SOCIAUX', weeks: 'Semaines 5-8', description: 'Calendrier éditorial 3 mois + scripts viraux. Objectif +15% de CA via Insta et TikTok. Tu postes avec un système.' },
  { number: '06', icon: '📋', title: 'FICHES TECHNIQUES', weeks: 'Semaines 4-6', description: 'Dosages parfaits, plats calibrés au gramme près. Plus de perte de marge à cause de portions trop généreuses.' },
  { number: '07', icon: '👥', title: 'RECRUTEMENT & ÉQUIPE', weeks: 'Semaines 6-9', description: 'Doc onboarding, semaine type nouveau profil, primes performance par paliers. Tu cadres ton équipe pour qu\'elle bosse sans toi.' },
  { number: '08', icon: '⚙️', title: 'ORGANISATION INTERNE', weeks: 'Semaines 7-10', description: 'QR code à table, borne, click & collect, outils malins. On automatise et on simplifie ton quotidien.' },
  { number: '09', icon: '🎓', title: 'COACHING DIRIGEANT', weeks: 'Continu (3 mois)', description: '45 min tous les 15 jours pendant 3 mois : comment prioriser, organiser, structurer ton business et ton recrutement.' },
  { number: '10', icon: '🛵', title: 'STRATÉGIE LIVRAISON', weeks: 'Semaines 8-10', description: 'Uber Eats / Deliveroo : structuration carte, prix à afficher, offres à mettre en place. On maximise les commandes rentables.' },
  { number: '11', icon: '📦', title: 'PACKAGING & MOCKUPS', weeks: 'Semaines 10-12', description: 'Packaging tendance et unique adapté à ta direction artistique. Sacs, boîtes, étiquettes — c\'est ta marque qui voyage.' },
];

// Development gains data
const DEVELOPMENT_GAINS = [
  { icon: '📈', label: 'Ticket moyen +2€ sur 80 couverts/jour', amount: '+4 800€ / mois' },
  { icon: '🎯', label: 'Stratégie réseaux sociaux (+15% CA)', amount: '+1 200€ / mois' },
  { icon: '🛡️', label: 'Validation prestataires (1 seule fois)', amount: '+8 à 15k€' },
  { icon: '🛵', label: 'Stratégie livraison optimisée', amount: '+1 200€ / mois' },
  { icon: '🍽️', label: 'Carte restructurée + food cost', amount: '+900€ / mois' },
  { icon: '⭐', label: 'Image de marque cohérente', amount: '+800€ / mois' },
];

// Pillar progress data
const PILLAR_PROGRESS = [
  { icon: '💰', name: 'Chiffres & Rentabilité', before: 35, after: 92 },
  { icon: '🍽️', name: 'Carte & Menu', before: 40, after: 92 },
  { icon: '🎨', name: 'Image de Marque', before: 30, after: 94 },
  { icon: '🛡️', name: 'Validation Prestataires', before: 10, after: 95 },
  { icon: '📱', name: 'Réseaux Sociaux', before: 25, after: 90 },
  { icon: '👥', name: 'Équipe & Recrutement', before: 30, after: 88 },
  { icon: '⚙️', name: 'Organisation', before: 20, after: 90 },
];

// Development Gain Row
const DevelopmentGainRow = ({ icon, label, amount, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -20 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.4, delay }}
      className="flex items-center justify-between py-4 border-b border-[#e0dbd4]"
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <span className="text-[#1a1a1a] text-sm sm:text-base">{label}</span>
      </div>
      <span className="text-green-500 font-bold text-lg whitespace-nowrap">{amount}</span>
    </motion.div>
  );
};

// Progress Bar with animation
const ProgressBarComparison = ({ icon, name, before, after, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 10 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay }}
      className="mb-6"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-[#1a1a1a] font-semibold">{name}</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex-1 flex items-center gap-2">
          <span className="text-red-500 text-sm font-bold w-10">{before}%</span>
          <div className="flex-1 h-3 bg-[#e0dbd4] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={isInView ? { width: `${before}%` } : {}}
              transition={{ duration: 0.8, delay: delay + 0.2 }}
              className="h-full bg-red-500 rounded-full"
            />
          </div>
        </div>
        <span className="text-[rgba(26,26,26,0.42)]">→</span>
        <div className="flex-1 flex items-center gap-2">
          <div className="flex-1 h-3 bg-[#e0dbd4] rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={isInView ? { width: `${after}%` } : {}}
              transition={{ duration: 1, delay: delay + 0.5 }}
              className="h-full bg-green-500 rounded-full"
            />
          </div>
          <span className="text-green-500 text-sm font-bold w-10">{after}%</span>
        </div>
      </div>
    </motion.div>
  );
};

const ACCESS_CODE = 'PRSNL2026'; // Code d'accès donné après l'appel

const SalesPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState(null);
  const [openModule, setOpenModule] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  // Cross-page personalization: read user context from /resultats if available
  const [userCtx, setUserCtx] = useState(null);
  useEffect(() => {
    setUserCtx(readUserContext());
  }, []);

  // Pick up Sabril CTA intent stashed in sessionStorage (cross-page navigation)
  useEffect(() => {
    let tab = null;
    try { tab = sessionStorage.getItem('sabril_cta_target_tab'); } catch (_) { return; }
    if (!tab) return;
    try { sessionStorage.removeItem('sabril_cta_target_tab'); } catch (_) {}
    setTimeout(() => {
      const pricingTop = document.querySelector('[data-testid="pricing-section-top"]');
      if (pricingTop) {
        const top = pricingTop.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      }
      setTimeout(() => {
        const tabBtn = document.querySelector(`[data-testid="pricing-tab-${tab}"]`);
        if (tabBtn) tabBtn.click();
      }, 850);
    }, 600);
  }, []);
  
  // Access control — unlocked by default (audit gratuit débouche directement sur l'offre)
  const [isUnlocked, setIsUnlocked] = useState(true);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState(false);

  // Check if already unlocked (localStorage)
  useEffect(() => {
    setIsUnlocked(true);
    localStorage.setItem('prsnl_access', 'true');
  }, []);
  
  const handleCodeSubmit = () => {
    if (codeInput.toUpperCase() === ACCESS_CODE) {
      setIsUnlocked(true);
      localStorage.setItem('prsnl_access', 'true');
      setCodeError(false);
    } else {
      setCodeError(true);
    }
  };
  
  // Sliders state
  const [panierMoyen, setPanierMoyen] = useState(12);
  const [clientsJour, setClientsJour] = useState(45);
  const [optimisationPlateforme, setOptimisationPlateforme] = useState(10);
  
  // Calculate impacts
  const caMensuelActuel = panierMoyen * clientsJour * 30;
  const caMensuelOptimise = (panierMoyen + 3) * (clientsJour + 15) * 30;
  const gainMensuel = caMensuelOptimise - caMensuelActuel;
  const gainAnnuel = gainMensuel * 12;
  const gainPlateforme = Math.round(caMensuelActuel * (optimisationPlateforme / 100));
  const totalGainMensuel = gainMensuel + gainPlateforme;
  const totalGainAnnuel = totalGainMensuel * 12;

  // Website upsell (+690€) — per pack
  const [addWebsiteRefonte, setAddWebsiteRefonte] = useState(false);
  const [addWebsiteOpening, setAddWebsiteOpening] = useState(false);

  // Subscription upsell (Réservations + Site · 39€/mois) — per pack
  const [addSubRefonte, setAddSubRefonte] = useState(false);
  const [addSubOpening, setAddSubOpening] = useState(false);

  const handleStripeCheckout = async (paymentType = 'full') => {
    setIsLoading(true);
    setLoadingType(paymentType);
    try {
      // Determine if website addon applies (only on full payment type)
      const addWebsite =
        (paymentType === 'full' && addWebsiteRefonte) ||
        (paymentType === 'opening' && addWebsiteOpening);
      const addSubscription =
        (paymentType === 'full' && addSubRefonte) ||
        (paymentType === 'opening' && addSubOpening);
      const response = await fetch(`${API_URL}/api/prsnl/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          payment_type: paymentType,
          add_website: addWebsite,
          add_subscription: addSubscription,
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        alert('Erreur lors de la création du paiement. Veuillez réessayer.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Erreur de connexion. Veuillez réessayer.');
    }
    setIsLoading(false);
    setLoadingType(null);
  };

  // Access code screen
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-[#f5f3f0] flex items-center justify-center px-6" data-testid="sales-page-locked">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-[#F05A1A]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">🔒</span>
          </div>
          
          <h1 className="font-bebas text-4xl text-[#1a1a1a] mb-3">ACCÈS RÉSERVÉ</h1>
          <p className="text-[rgba(26,26,26,0.58)] mb-8">
            Cette page est réservée aux restaurateurs ayant effectué un appel stratégique.
          </p>
          
          <div className="bg-white rounded-2xl p-6 mb-6">
            <label className="text-[rgba(26,26,26,0.58)] text-sm block mb-3">Code d'accès</label>
            <input
              type="text"
              value={codeInput}
              onChange={(e) => {
                setCodeInput(e.target.value.toUpperCase());
                setCodeError(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleCodeSubmit()}
              placeholder="Entrez votre code"
              className={`w-full bg-[#f5f3f0] border-2 ${codeError ? 'border-red-500' : 'border-[#e0dbd4]'} text-[#1a1a1a] text-xl px-5 py-4 rounded-xl text-center font-bold tracking-widest uppercase outline-none focus:border-[#F05A1A] transition-colors`}
            />
            {codeError && (
              <p className="text-red-500 text-sm mt-2">Code incorrect</p>
            )}
          </div>
          
          <button
            onClick={handleCodeSubmit}
            className="w-full bg-[#F05A1A] hover:bg-[#D94D0F] text-white font-bold py-4 rounded-xl transition-all"
          >
            ACCÉDER À LA PAGE
          </button>
          
          <div className="mt-8 pt-8 border-t border-[#e0dbd4]">
            <p className="text-[rgba(26,26,26,0.42)] text-sm mb-4">Pas encore de code ?</p>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#F05A1A] font-semibold hover:underline"
            >
              <Calendar className="w-5 h-5" />
              Réserver mon appel stratégique gratuit
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f3f0]" data-testid="sales-page">

      {/* Bannière de continuité — affichée uniquement si user vient de /resultats */}
      {userCtx && userCtx.restoName && (() => {
        const suggested = getSuggestedOffer(userCtx);
        const suggestedLabel = getSuggestedOfferLabel(suggested);
        return (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] border-b border-[#F05A1A]/30 px-4 py-3"
          data-testid="prsnl-continuity-banner"
        >
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-base">👋</span>
              <p className="text-white/90 text-xs sm:text-sm leading-snug">
                <span className="text-[#F05A1A] font-black uppercase tracking-wider text-[10px] mr-2">Suite de ton audit</span>
                {userCtx.firstName && <strong className="text-white">{userCtx.firstName}, </strong>}
                voici comment je règle « <strong className="text-white">{userCtx.restoName}</strong> »
                {suggestedLabel && (
                  <> · offre recommandée : <strong className="text-[#10b981]">{suggestedLabel}</strong></>
                )}
              </p>
            </div>
            <Link
              to={userCtx.auditId ? `/resultats/${userCtx.auditId}` : '/diagnostic'}
              className="text-white/55 hover:text-white text-[11px] underline whitespace-nowrap"
              data-testid="prsnl-continuity-back"
            >
              ← Retour à mon audit
            </Link>
          </div>
        </motion.div>
        );
      })()}

      {/* HERO - TON RESTO EST RENTABLE */}
      <section className="flex flex-col items-center justify-center px-5 sm:px-6 pt-16 pb-10 bg-gradient-to-b from-[#f5f3f0] to-[#f5f3f0]">

        {/* === MOBILE-ONLY EDITORIAL HERO CARD === */}
        <div className="sm:hidden w-full max-w-md mx-auto mb-6">
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-3xl overflow-hidden mb-4"
            data-testid="prsnl-hero-mobile-card"
            style={{
              background: 'linear-gradient(180deg, #0F0F12 0%, #2A1810 100%)',
              boxShadow: '0 24px 50px rgba(0,0,0,0.40), 0 0 0 1px rgba(240,90,26,0.30) inset',
              aspectRatio: '4 / 5',
            }}
          >
            {/* Animated mesh glows */}
            <motion.div
              className="absolute -top-20 -left-20 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(240,90,26,0.30) 0%, transparent 60%)' }}
              animate={{ scale: [1, 1.15, 1], opacity: [0.55, 0.85, 0.55] }}
              transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute -bottom-24 -right-16 w-72 h-72 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.25) 0%, transparent 60%)' }}
              animate={{ scale: [1.05, 1, 1.05], opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            />

            {/* Top-left: Coaching premium badge */}
            <div className="absolute top-3.5 left-3.5 z-10 flex items-center gap-1.5 rounded-full py-1 px-2.5 bg-emerald-500/20 backdrop-blur-md border border-emerald-400/40">
              <motion.span
                className="w-1.5 h-1.5 rounded-full bg-emerald-400"
                animate={{ opacity: [1, 0.35, 1] }}
                transition={{ duration: 1.4, repeat: Infinity }}
              />
              <span className="text-[9px] font-black uppercase tracking-wider text-emerald-300">PRSNL Coach</span>
            </div>

            {/* Top-right: Limited spots sticker */}
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: -6 }}
              transition={{ delay: 0.5, type: 'spring', stiffness: 280 }}
              className="absolute top-3.5 right-3.5 z-10 rounded-2xl px-2.5 py-1.5 flex flex-col items-center"
              style={{
                background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)',
                boxShadow: '0 10px 24px rgba(240,90,26,0.55), 0 0 0 2px rgba(255,255,255,0.18) inset',
              }}
            >
              <span className="font-bebas text-white leading-none" style={{ fontSize: 18 }}>4</span>
              <span className="text-white/90 text-[8px] font-bold uppercase tracking-wider mt-0.5">places/mois</span>
            </motion.div>

            {/* Pixar avatar centered top */}
            <div className="absolute top-12 left-0 right-0 flex justify-center z-[2]">
              <SabrilPixarAvatar size={96} glow />
            </div>

            {/* Headline */}
            <div className="absolute inset-x-0 top-[42%] z-[2] px-5 text-center">
              <p className="text-[10px] font-black tracking-[3px] uppercase mb-1.5" style={{ color: '#F05A1A' }}>
                Accompagnement PRSNL · 1-à-1
              </p>
              <h1
                className="font-bebas leading-[0.88] tracking-tight text-white mb-2"
                style={{ fontSize: 'clamp(34px, 10vw, 52px)' }}
              >
                TON RESTO
                <br />
                <span
                  style={{
                    background: 'linear-gradient(135deg, #FBBF24 0%, #F05A1A 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >RENTABLE.</span>
                <br />
                AUTONOME. <span style={{ color: '#FFFFFF' }}>DUPLICABLE.</span>
              </h1>
              <p className="text-white/70 text-[11px] leading-snug italic">
                « On construit ta marque, tes process, tes équipes. <strong className="text-emerald-300">Avec toi, sur le terrain.</strong> »
              </p>
            </div>

            {/* Bottom stats */}
            <div className="absolute bottom-0 left-0 right-0 p-4 z-[2]">
              <div className="grid grid-cols-3 gap-2 mb-2.5">
                <div className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12">
                  <div className="font-bebas text-white text-xl leading-none">11</div>
                  <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">modules</div>
                </div>
                <div className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12">
                  <div className="font-bebas text-[#FBBF24] text-xl leading-none">2-9j</div>
                  <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">sur place</div>
                </div>
                <div className="rounded-xl px-2 py-2 text-center bg-white/8 backdrop-blur-md border border-white/12">
                  <div className="font-bebas text-emerald-300 text-xl leading-none">90j</div>
                  <div className="text-white/65 text-[8.5px] font-bold uppercase tracking-wider mt-1">durée</div>
                </div>
              </div>
            </div>
            <div
              className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
              style={{ background: 'linear-gradient(0deg, rgba(15,15,18,0.95) 0%, transparent 100%)' }}
            />
          </motion.div>

          {/* CTA mobile — opens Sabril Closer instead of WhatsApp */}
          <motion.button
            type="button"
            onClick={() => {
              try { window.dispatchEvent(new CustomEvent('sabril:open', { detail: { intent: 'project' } })); } catch (e) { /* noop */ }
            }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileTap={{ scale: 0.97 }}
            className="w-full flex items-center justify-center gap-2 px-7 py-4 rounded-2xl font-extrabold text-white text-base shadow-xl"
            style={{ background: '#F05A1A' }}
            data-testid="prsnl-hero-cta-mobile"
          >
            Parler à Sabril
          </motion.button>
          <a
            href="#packs"
            className="mt-2 w-full flex items-center justify-center gap-2 px-7 py-3 rounded-2xl font-bold text-sm border bg-white text-[#1a1a1a] border-[#1a1a1a]/15"
          >
            Voir les formules ↓
          </a>
        </div>

        {/* === DESKTOP HERO (unchanged) === */}
        <div className="hidden sm:flex flex-col items-center justify-center w-full">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-bebas text-5xl sm:text-6xl md:text-7xl text-center text-[#1a1a1a] leading-tight mb-8"
        >
          TON RESTO EST
          <span className="block">RENTABLE.</span>
          <span className="block">AUTONOME.</span>
          <span className="block">DUPLICABLE.</span>
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-[#F05A1A] text-lg sm:text-xl text-center mb-8"
        >
          Refonte ou ouverture — accompagnement sur mesure
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-wrap justify-center gap-3 mb-12"
        >
          <Badge>11 modules</Badge>
          <Badge>2 à 9 jours sur place</Badge>
          <Badge>Résultats garantis</Badge>
        </motion.div>

        {/* AVATAR — Salutation Sabril (personnalisée si user vient de /resultats) */}
        <div className="mb-10 flex justify-center w-full">
          <AnimatedAvatar
            size="lg"
            position="right"
            bubbleArrow="left"
            delay={0.9}
            testid="hero-avatar"
            message={
              userCtx && userCtx.restoName
                ? [
                    `Salut ${userCtx.firstName || 'à toi'} 👋 J'ai vu ton diagnostic pour « ${userCtx.restoName} ».`,
                    userCtx.projectStatus === 'en_ouverture'
                      ? `Tu prépares l'ouverture — c'est exactement le moment de tout construire bien dès le début.`
                      : userCtx.projectStatus === 'multi_resto'
                      ? `Plusieurs restos à structurer — c'est mon terrain de jeu favori.`
                      : userCtx.foodCost && userCtx.foodCost > 32
                      ? `Food cost à ${userCtx.foodCost}% — c'est exactement ce qu'on règle ensemble.`
                      : `On a tout pour transformer ton resto. Je te montre comment.`,
                    `Choisis ta formule ci-dessous — je te guide étape par étape.`,
                  ]
                : [
                    "Salut, c'est Sabril 👋 Je vais t'expliquer comment on transforme ton resto.",
                    "On a accompagné +40 restos. Aujourd'hui, c'est ton tour.",
                    "Choisis ta formule ci-dessous — je te guide étape par étape.",
                  ]
            }
          />
        </div>
        
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-[rgba(26,26,26,0.58)] text-center max-w-md"
        >
          Choisis ta formule ci-dessous · Je te laisse découvrir le programme juste après ↓
        </motion.p>
        </div>
        {/* /Desktop hero */}
      </section>

      {/* CTA Section — Pricing App (Tabs) — moved right after hero for direct conversion */}
      {/* SECTION : POURQUOI 1 RESTO SUR 3 FERME — premium app-style, light theme */}
      <section className="px-4 sm:px-6 py-14 bg-[#f5f3f0]" data-testid="why-this-matters-section">
        <div className="max-w-3xl mx-auto">
          {/* Stat alarmante — carte blanche premium */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 mb-6 border border-[#e0dbd4] shadow-[0_8px_24px_rgba(26,26,26,0.06)] text-center">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] mb-3">
              ⚠️ Réalité du marché 2026
            </p>
            <p className="font-bebas text-6xl sm:text-7xl text-[#F05A1A] leading-none mb-3 tabular-nums">
              1 RESTO SUR 3
            </p>
            <p className="font-bebas text-2xl sm:text-3xl text-[#1a1a1a] leading-tight">
              ferme ses portes avant <span className="text-[#F05A1A]">3 ans</span>
            </p>
            <p className="text-sm text-[#1a1a1a]/65 max-w-xl mx-auto mt-4 leading-relaxed">
              Pas par manque de talent culinaire. Pas par manque d'envie. Toujours pour <strong className="text-[#1a1a1a]">les mêmes 7 raisons</strong> — toutes évitables avec le bon cadre.
            </p>
          </div>

          {/* CARTE 3D — France avec fermetures live */}
          <div className="mb-10">
            <FranceClosureMap />
          </div>

          {/* Les 7 causes — cards blanches style bento (comme tes screenshots) */}
          <p className="text-[10px] font-black uppercase tracking-widest text-[#1a1a1a]/55 mb-4 text-center">
            🚨 Les 7 causes que ton accompagnement va attaquer
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-10">
            {[
              { ti: "Aucun process structuré", de: "Chaque jour réinventé, dépendant des humeurs · zéro reproductibilité" },
              { ti: "Recrutement émotionnel & turnover massif", de: "On embauche au feeling, on perd 3 mois à former, ça part en 6 mois" },
              { ti: "Aucune gestion fine des coûts", de: "Food cost à l'instinct · marge qui fond · zéro pilotage" },
              { ti: "0 visibilité, 0 marque", de: "Aucune identité forte · invisible sur Insta/TikTok · personne ne sait qui tu es" },
              { ti: "Concept non duplicable", de: "Tout repose sur toi · impossible d'ouvrir un 2ème resto sans tout refaire" },
              { ti: "Pas d'industrialisation", de: "Cuisine artisanale = OK pour 1 resto, mortelle pour un groupe" },
              { ti: "Salariés sans objectifs chiffrés", de: "Pas de KPI mensuel · 0 culture de la performance · rémunération déconnectée du résultat" },
            ].map((c, i) => (
              <div
                key={i}
                className="flex items-start gap-3 p-3.5 rounded-2xl bg-white border border-[#e0dbd4] hover:border-[#F05A1A]/40 hover:shadow-[0_4px_12px_rgba(240,90,26,0.08)] transition-all"
                data-testid={`failure-cause-${i}`}
              >
                <span className="inline-flex items-center justify-center flex-shrink-0 mt-0.5 w-7 h-7 rounded-full bg-[#F05A1A] text-white text-[12px] font-black shadow-sm">
                  {i + 1}
                </span>
                <span className="flex-1 text-[13px] leading-snug">
                  <strong className="block text-[#1a1a1a] mb-0.5">{c.ti}</strong>
                  <span className="text-[12px] text-[#1a1a1a]/60">{c.de}</span>
                </span>
              </div>
            ))}
          </div>

          {/* AVANT / APRÈS — 2 cartes distinctes rouge vs vert (style app) */}
          <div className="mb-10" data-testid="before-after-section">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#1a1a1a]/55 mb-4 text-center">
              📊 La vraie différence en chiffres
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* AVANT — carte blanche rouge */}
              <div
                className="rounded-2xl p-5 bg-white border-2 shadow-[0_4px_14px_rgba(239,68,68,0.10)]"
                style={{ borderColor: 'rgba(239,68,68,0.30)' }}
                data-testid="before-block"
              >
                <span className="inline-block px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider mb-3" style={{ background: 'rgba(239,68,68,0.10)', color: '#dc2626' }}>
                  ❌ Sans accompagnement
                </span>
                <p className="font-bebas text-xl text-[#1a1a1a]/70 leading-tight mb-4">
                  Le pilotage à l'instinct
                </p>
                <ul className="space-y-2.5">
                  {[
                    { k: 'Food cost', v: '38–42%', extra: 'instinct cuisinier' },
                    { k: 'Turnover annuel', v: '80%', extra: 'recrut. émotionnel' },
                    { k: 'Marge nette', v: '4–7%', extra: 'mal pilotée' },
                    { k: 'Process documentés', v: '0', extra: 'tout dans la tête' },
                    { k: 'Visibilité / marque', v: 'Faible', extra: 'invisible sur le marché' },
                    { k: 'Duplicable ?', v: 'Non', extra: 'tout repose sur toi' },
                    { k: 'KPI salariés', v: 'Aucun', extra: 'pas de culture perf' },
                  ].map((row) => (
                    <li key={row.k} className="flex items-center justify-between gap-3 py-1.5 border-b border-[#1a1a1a]/5 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-[#1a1a1a]/80 font-bold leading-tight">{row.k}</p>
                        <p className="text-[10px] text-[#1a1a1a]/40 italic leading-tight mt-0.5">{row.extra}</p>
                      </div>
                      <span className="font-bebas text-xl text-red-500 leading-none tabular-nums whitespace-nowrap">{row.v}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* APRÈS — carte blanche verte (style dashboard "Excellent" du screenshot) */}
              <div
                className="rounded-2xl p-5 bg-gradient-to-br from-[#10b981] to-[#059669] border-2 border-[#10b981] shadow-[0_8px_24px_rgba(16,185,129,0.25)] text-white"
                data-testid="after-block"
              >
                <span className="inline-block px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider mb-3 bg-white/20 text-white">
                  ✅ Avec PRSNL
                </span>
                <p className="font-bebas text-xl text-white leading-tight mb-4">
                  Le pilotage industriel
                </p>
                <ul className="space-y-2.5">
                  {[
                    { k: 'Food cost', v: '26–30%', extra: 'calibré par fiche tech' },
                    { k: 'Turnover annuel', v: '20–30%', extra: 'recrut. sur stats' },
                    { k: 'Marge nette', v: '12–18%', extra: 'pilotée chaque mois' },
                    { k: 'Process documentés', v: '100%', extra: 'manuel opératoire' },
                    { k: 'Visibilité / marque', v: 'Forte', extra: 'identifiable, mémorable' },
                    { k: 'Duplicable ?', v: 'OUI', extra: 'prêt pour la franchise' },
                    { k: 'KPI salariés', v: '100%', extra: 'objectif mensuel chiffré' },
                  ].map((row) => (
                    <li key={row.k} className="flex items-center justify-between gap-3 py-1.5 border-b border-white/15 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] text-white font-bold leading-tight">{row.k}</p>
                        <p className="text-[10px] text-white/75 italic leading-tight mt-0.5">{row.extra}</p>
                      </div>
                      <span className="font-bebas text-xl text-white leading-none tabular-nums whitespace-nowrap">{row.v}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-[11px] text-[#1a1a1a]/45 text-center mt-4 italic leading-relaxed">
              Fourchettes observées sur les restaurants accompagnés (concepts premium · marchés exigeants).<br/>
              Pas de promesse magique — juste le résultat d'un pilotage industriel rigoureux.
            </p>
          </div>

          {/* Ce qu'on construit — carte blanche premium accent orange */}
          <div className="rounded-2xl p-5 sm:p-6 bg-white border-2 border-[#F05A1A] shadow-[0_8px_24px_rgba(240,90,26,0.12)]">
            <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] mb-3">
              ✅ Ce qu'on construit ensemble à la place
            </p>
            <p className="font-bebas text-2xl sm:text-3xl text-[#1a1a1a] leading-tight mb-3">
              Une marque <span className="text-[#F05A1A]">industrielle</span>, duplicable, franchisable
            </p>
            <ul className="space-y-1.5 text-[13px] text-[#1a1a1a]/80 leading-snug">
              <li>✓ Système de recrutement basé sur la performance · <strong className="text-[#1a1a1a]">1 salarié = 100% des objectifs à atteindre chaque mois</strong> · zéro émotion, que des stats</li>
              <li>✓ Gestion des coûts et des marges <strong className="text-[#1a1a1a]">pensée comme un industriel</strong></li>
              <li>✓ Process documentés · format <strong className="text-[#1a1a1a]">duplicable</strong> dans n'importe quelle ville · prêt pour la franchise</li>
              <li>✓ Marque forte, identifiable, visible <strong className="text-[#1a1a1a]">avant même l'ouverture</strong> — rien laissé au hasard, 360° verrouillé</li>
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION : LE MOMENT DE VÉRITÉ — "C'EST DE MA FAUTE" */}
      <OwnerRealizationSection />

      {/* SIMULATEUR — Build-up de conviction avant la révélation des offres */}
      <section className="px-6 py-14 bg-[#f5f3f0]">
        <div className="max-w-2xl mx-auto">
          <p className="text-[10px] font-black uppercase tracking-[3px] text-[#F05A1A] text-center mb-2">
            ⚡ Avant de te montrer comment je le règle
          </p>
          <h2 className="text-center font-bebas text-3xl sm:text-4xl text-[#1a1a1a] mb-2">VOIS CE QUE TU LAISSES SUR LA TABLE</h2>
          <p className="text-center text-[rgba(26,26,26,0.5)] text-sm mb-8">Déplace les curseurs · vois ce que tu pourrais gagner</p>

          <div className="space-y-6">
            <InteractiveSlider
              id="slider-panier"
              label="Panier moyen actuel"
              min={8}
              max={25}
              value={panierMoyen}
              onChange={setPanierMoyen}
              unit="€"
              impact={`+3€/client × ${clientsJour} clients × 30j = +${(3 * clientsJour * 30).toLocaleString('fr-FR')}€/mois`}
            />
            <InteractiveSlider
              id="slider-clients"
              label="Nombre de clients / jour"
              min={20}
              max={100}
              value={clientsJour}
              onChange={setClientsJour}
              unit=" clients"
              impact={`+15 clients × ${panierMoyen}€ × 30j = +${(15 * panierMoyen * 30).toLocaleString('fr-FR')}€/mois`}
            />
            <InteractiveSlider
              id="slider-plateforme"
              label="Optimisation plateformes (Uber/Deliveroo)"
              min={5}
              max={30}
              value={optimisationPlateforme}
              onChange={setOptimisationPlateforme}
              unit="%"
              impact={`${optimisationPlateforme}% sur ${caMensuelActuel.toLocaleString('fr-FR')}€ = +${gainPlateforme.toLocaleString('fr-FR')}€/mois`}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 bg-white rounded-2xl p-6 border-2 border-green-500/30 shadow-[0_6px_24px_rgba(16,185,129,0.10)] text-center"
          >
            <p className="text-[10px] font-black uppercase tracking-widest text-green-600 mb-2">💚 Ton potentiel identifié</p>
            <p className="font-bebas text-5xl sm:text-6xl text-green-600 leading-none tracking-tight">
              +<AnimatedCounter value={totalGainAnnuel} suffix="€" />
            </p>
            <p className="text-[rgba(26,26,26,0.6)] text-sm mt-1">par an · soit +{totalGainMensuel.toLocaleString('fr-FR')}€/mois</p>
            <p className="text-[#1a1a1a]/55 text-xs italic mt-3 leading-snug">
              C'est ce qui dort dans ton resto en ce moment.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION : RÉVÉLATION CINÉMATIQUE AVANT LE PRICING (personnalisée si user vient de /resultats) */}
      <PricingRevealGate
        onChooseDoor={(door) => {
          setTimeout(() => {
            const tabBtn = document.querySelector(`[data-testid="pricing-tab-${door === '1 day' ? 'audit_day' : door}"]`);
            if (tabBtn) tabBtn.click();
          }, 800);
        }}
        {...(userCtx && userCtx.restoName ? {
          customKicker: userCtx.firstName
            ? `— ${userCtx.firstName}, maintenant que tu sais d'où ça vient —`
            : `— Maintenant que tu sais d'où ça vient —`,
          customTitleTop: 'VOICI COMMENT',
          customTitleBottom: 'JE LE RÈGLE.',
          customSub: (
            <>
              Pour <strong className="text-white">« {userCtx.restoName} »</strong>, 4 portes possibles.
              <br />
              <span className="text-white font-semibold">0 promesse claquée</span>. Choisis ton moment.
            </>
          ),
        } : {})}
      />

      {/* SECTION PRICING */}
      <section className="px-4 sm:px-6 py-12 bg-[#f5f3f0]" data-testid="pricing-section-top">
        <div className="max-w-2xl mx-auto">
          {/* Premium gate banner — light premium card */}
          <div
            className="rounded-2xl p-4 mb-6 flex items-center gap-3 bg-white border-2 border-[#F05A1A] shadow-[0_6px_20px_rgba(240,90,26,0.12)]"
            data-testid="premium-gate-banner"
          >
            <div
              className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-black text-base text-white shadow-md"
              style={{ background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)' }}
            >
              ⚡
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-[#F05A1A] leading-tight">
                Accompagnement premium · Sélection en amont
              </p>
              <p className="text-[#1a1a1a] text-sm font-bold leading-snug mt-1">
                Refonte : restos qui font déjà <span className="text-[#F05A1A]">30 000€+/mois</span> · Ouverture : <span className="text-[#F05A1A]">concept premium</span> avec budget &amp; sens
              </p>
              <p className="text-[#1a1a1a]/60 text-[11px] leading-snug mt-1">
                Pas dans cette zone ? Prends d'abord le plan d'action à 47€ pour grimper.
              </p>
            </div>
          </div>

          <div className="text-center mb-6">
            <p className="text-[10px] font-black uppercase tracking-widest mb-2 text-[#F05A1A]">
              🏆 Choisis ta formule
            </p>
            <h2 className="font-bebas text-4xl sm:text-5xl leading-[1.05] text-[#1a1a1a]">
              Prêt à passer<br/>
              <span className="text-[#F05A1A]">au niveau supérieur ?</span>
            </h2>
            <p className="text-[#1a1a1a]/55 text-[12px] mt-3 max-w-md mx-auto leading-relaxed">
              <strong className="text-[#F05A1A]">Ouverture</strong> = notre produit signature · le format où tout mon travail se voit · seulement <strong>4 places/mois</strong>.
            </p>
          </div>

          {/* AVATAR — Pointe les prix */}
          <div className="mb-8 flex justify-center">
            <AnimatedAvatar
              size="md"
              position="right"
              bubbleArrow="left"
              pointing
              delay={0.2}
              testid="pricing-avatar"
              message={[
                "L'Ouverture, c'est ma signature. C'est là où je donne tout — du concept au D-Day.",
                "Refonte si ton resto tourne. Groupe si tu scales. 1 Day si tu veux tester.",
                "Les places sont limitées par offre — regarde le compteur en direct 👇",
              ]}
            />
          </div>

          {/* Pricing tabs : pre-select recommended tab if user came from /resultats */}
          {(() => {
            const suggested = getSuggestedOffer(userCtx);
            return (
              <PricingTabs
                initialTab={suggested || 'ouverture'}
                highlightTabId={suggested || null}
                hidePrice={true}
              />
            );
          })()}
        </div>
      </section>

      {/* Cockpits clients réels — 4 cas anonymisés rotatifs (preuve avant pricing) */}
      <CockpitProofCarousel />

      {/* DASHBOARD DE PILOTAGE — Bientôt disponible (inclus dans Refonte/Ouverture/Groupe) */}
      <DashboardPilotageSection />

      {/* CE QUE TU VIS / SYSTÈME / MODULES / DÉVELOPPEMENT / ROI — sections retirées pour resserrer le flow et garder l'attention sur le pricing */}

      {/* C'EST POUR TOI SI... — version DARK condensée 2 colonnes */}
      <section
        className="relative px-6 py-14 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
      >
        <PremiumDarkAtmosphere variant="default" />

        <div className="relative max-w-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-bebas text-3xl sm:text-4xl text-white text-center mb-6"
          >
            C'EST POUR TOI <span className="text-[#F05A1A]">SI...</span>
          </motion.h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* OUI — vert sur dark */}
            <div className="bg-white/[0.04] backdrop-blur-sm rounded-2xl p-5 border-l-4 border-green-500 border-t border-r border-b border-white/10 shadow-[0_4px_14px_rgba(16,185,129,0.10)]">
              <p className="text-[10px] font-black uppercase tracking-widest text-green-400 mb-3">✅ Oui, c'est pour toi</p>
              <ul className="space-y-2 text-[13px] text-white/85 leading-snug">
                <li>· Tu fais déjà <strong className="text-white">15 000€+ de CA/mois</strong></li>
                <li>· Tu veux <strong className="text-white">structurer pour scaler</strong></li>
                <li>· T'es prêt à <strong className="text-white">investir sur toi</strong></li>
                <li>· Tu veux des résultats, pas du blabla</li>
              </ul>
            </div>

            {/* NON — rouge sur dark */}
            <div className="bg-white/[0.04] backdrop-blur-sm rounded-2xl p-5 border-l-4 border-red-400 border-t border-r border-b border-white/10">
              <p className="text-[10px] font-black uppercase tracking-widest text-red-400 mb-3">❌ C'est pas pour toi</p>
              <ul className="space-y-2 text-[13px] text-white/85 leading-snug">
                <li>· Tu cherches une <strong className="text-white">solution miracle</strong></li>
                <li>· Tu veux pas <strong className="text-white">changer tes habitudes</strong></li>
                <li>· Tu veux juste une formation passive</li>
                <li>· Ton resto fait moins de 10 000€/mois</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="px-6 py-16">
        <div className="max-w-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-bebas text-4xl text-[#1a1a1a] text-center mb-2"
          >
            ILS L'ONT FAIT.
          </motion.h2>
          <p className="text-[rgba(26,26,26,0.42)] text-center mb-10">Résultats de restaurateurs accompagnés</p>
          
          <div className="space-y-4">
            {TESTIMONIALS.map((testimonial, i) => (
              <TestimonialCard key={i} {...testimonial} delay={i * 0.15} />
            ))}
          </div>
        </div>
      </section>

      {/* GARANTIE */}
      <section className="px-6 py-16 bg-[#f5f3f0]">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl p-8 border-2 border-[#F05A1A] text-center shadow-[0_12px_40px_rgba(240,90,26,0.18)]"
          >
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6 shadow-lg" style={{ background: 'linear-gradient(135deg, #F05A1A 0%, #FF7A3D 100%)' }}>
              <Shield className="w-10 h-10 text-white" />
            </div>
            <span className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-3 bg-[#F05A1A]/10 text-[#F05A1A]">
              Engagement long terme
            </span>
            <h2 className="font-bebas text-4xl sm:text-5xl text-[#1a1a1a] mb-4 leading-none">ON CONSTRUIT DES MARQUES DURABLES</h2>
            <p className="text-[#1a1a1a]/70 text-lg mb-4 leading-relaxed">
              Pas de promesse marketing claquée. Pas de chiffre miracle sorti d'un chapeau.
            </p>
            <p className="text-[#1a1a1a]/75 text-base mb-6 leading-relaxed">
              Ce qu'on construit ensemble : une <span className="text-[#F05A1A] font-bold">marque rentable par sa croissance</span>, pilotable comme un industriel, <span className="text-[#1a1a1a] font-bold">duplicable</span> et <span className="text-[#1a1a1a] font-bold">scalable</span>. Tu gagnes plus chaque année — pas en grattant 50€ sur les fournisseurs, mais en faisant grossir une marque qui a du sens.
            </p>
            <div className="pt-5 border-t border-[#e0dbd4]">
              <p className="text-[10px] font-black uppercase tracking-[3px] text-[#F05A1A] mb-2">
                Le terrain qu'on connaît par cœur
              </p>
              <p className="font-bebas text-[#1a1a1a] text-3xl sm:text-4xl tracking-wide leading-none">
                LÀ OÙ J'AI VÉCU. LÀ OÙ J'AI <span className="text-[#F05A1A]">SERVI</span> MES CLIENTS.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['Paris', 'Cannes', 'Marrakech', 'Bangtao', 'New York', 'Miami', 'Marbella'].map((city) => (
                  <span
                    key={city}
                    className="px-3 py-1.5 rounded-full bg-[#1a1a1a] text-white text-xs font-bold tracking-wide"
                    data-testid={`city-chip-${city.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {city}
                  </span>
                ))}
              </div>
              <p className="text-[#1a1a1a]/65 text-sm mt-4 leading-relaxed">
                7 villes où <span className="text-[#1a1a1a] font-semibold">le client a du goût, du niveau d'attente, et zéro patience pour l'amateurisme</span>. C'est <em>ce</em> niveau d'exécution que j'apporte à chaque resto qu'on accompagne — <span className="text-[#F05A1A] font-bold">peu importe ta ville</span>.
              </p>
            </div>

            {/* Background publicitaire × restauration — preuve d'expertise media + terrain */}
            <div className="mt-5 pt-5 border-t border-[#e0dbd4]">
              <p className="text-[10px] font-black uppercase tracking-[3px] text-[#F05A1A] mb-2">
                Et avant la restauration…
              </p>
              <p className="font-bebas text-[#1a1a1a] text-2xl sm:text-3xl tracking-wide leading-tight mb-3">
                +10 ANS À CONSTRUIRE DES <span className="text-[#F05A1A]">MARQUES</span> EN PUBLICITÉ
              </p>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="ad-experience-chips">
                <span className="px-3 py-1.5 rounded-full bg-[#F05A1A] text-white text-xs font-black tracking-wide uppercase">BFM</span>
                <span className="px-3 py-1.5 rounded-full bg-[#1a1a1a] text-white text-xs font-black tracking-wide uppercase">Alice Media</span>
                <span className="px-3 py-1.5 rounded-full bg-[#1a1a1a] text-white text-xs font-black tracking-wide uppercase">Communication PSG</span>
              </div>
              <p className="text-[#1a1a1a]/70 text-sm leading-relaxed">
                Avant d'ouvrir et d'accompagner des restos, j'ai passé des années à <strong className="text-[#1a1a1a]">construire et faire grossir des marques</strong> côté média &amp; communication. C'est ce qui me permet aujourd'hui de croiser <span className="text-[#F05A1A] font-bold">expertise publicitaire × terrain restauration</span> — deux mondes que personne ne sait vraiment marier. Tu en bénéficies sur chaque levier : marque, visibilité, narratif, conversion.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ — DARK */}
      <section
        className="relative px-6 py-16 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
      >
        <PremiumDarkAtmosphere variant="default" />
        <div className="relative max-w-2xl mx-auto">
          <p className="text-[10px] font-black uppercase tracking-[3px] text-[#F05A1A] text-center mb-2">
            ❓ On lève les objections
          </p>
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-bebas text-4xl text-white text-center mb-10"
          >
            QUESTIONS FRÉQUENTES
          </motion.h2>

          <div className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            {FAQ_DATA.map((faq, i) => (
              <FAQItem
                key={i}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === i}
                onToggle={() => setOpenFAQ(openFAQ === i ? null : i)}
                dark
              />
            ))}
          </div>
        </div>
      </section>

      {/* PLACES LIMITÉES — DARK */}
      <section
        className="relative px-6 py-12 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #1a0e0e 0%, #2a1414 100%)' }}
      >
        <PremiumDarkAtmosphere variant="orange" />
        <div className="relative max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#F05A1A]/25 to-[#F05A1A]/5 backdrop-blur-sm rounded-2xl p-6 border border-[#F05A1A]/50 text-center shadow-[0_8px_32px_rgba(240,90,26,0.25)]"
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <Users className="w-5 h-5 text-[#F05A1A]" />
              <span className="text-[#F05A1A] font-bold uppercase tracking-wide">Places limitées</span>
            </div>
            <p className="font-bebas text-5xl text-white mb-2">
              SEULEMENT <span className="text-[#F05A1A]">{SPOTS_LEFT}</span> PLACES
            </p>
            <p className="text-white/65">
              Pour garantir un accompagnement de qualité, j'accompagne maximum 5 restaurateurs en même temps.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Bouton Diagnostic — DARK */}
      <section
        className="relative px-6 pb-24 pt-8"
        style={{ background: 'linear-gradient(180deg, #2a1414 0%, #1a0e0e 100%)' }}
      >
        <div className="max-w-2xl mx-auto">
          {/* AVATAR — Push final */}
          <div className="mb-8 flex justify-center">
            <AnimatedAvatar
              size="lg"
              position="right"
              bubbleArrow="left"
              pulse
              testid="cta-final-avatar"
              message={[
                "Pas encore prêt ? Fais le diagnostic gratuit — on voit où tu en es.",
                "5 minutes. Aucun engagement. Tu repars avec ton score et ton plan.",
              ]}
            />
          </div>
          <Link
            to="/diagnostic"
            className="block w-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/15 hover:border-[#F05A1A]/40 text-white font-bold py-4 rounded-xl text-center transition-all backdrop-blur-sm"
          >
            Faire le diagnostic gratuit de mon restaurant
          </Link>
        </div>
      </section>

      {/* Floating Sabril Closer (AI sales agent powered by Claude Sonnet) */}
      <SabrilCloserBubble page="prsnl" />

      {/* Custom slider styles */}
      <style>{`
        .slider-input {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
        }
        .slider-input::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: white;
          border: 4px solid #F05A1A;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3), 0 0 20px rgba(240,90,26,0.4);
          margin-top: -4px;
        }
        .slider-input::-moz-range-thumb {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: white;
          border: 4px solid #F05A1A;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3), 0 0 20px rgba(240,90,26,0.4);
        }
        .slider-input::-webkit-slider-runnable-track {
          height: 8px;
          background: transparent;
        }
        .slider-input::-moz-range-track {
          height: 8px;
          background: transparent;
        }
        .slider-input:focus {
          outline: none;
        }
      `}</style>
      {/* === Cross-sell: les 2 autres offres === */}
      <div className="px-4 sm:px-6 max-w-4xl mx-auto pb-8">
        <OffersQuickAccess
          variant="light"
          kicker="🎯 Autres offres"
          title="Tu veux aller plus loin ?"
          hideCurrentPage={true}
        />
      </div>
    </div>
  );
};

export default SalesPage;
